// Where a bullet ends: the world hit or the first player hitbox on the way, whichever is closer.
// The players are the ones the client displays (interpolated), the hit test is the server's own
// (`gr_core::hitbox` through wasm), so a tracer stops in the body the server's bullet hits.

import type { InterpolatedPlayer } from "../net";
import { PlayerFlags } from "../wasm/types";
import type { Vec3Like } from "../wasm/types";

/** A player a bullet can hit, as displayed. */
export interface ShotTarget {
  id: number;
  /** Feet position. */
  pos: Vec3Like;
  /** 0 standing .. 1 crouched. */
  crouchFrac: number;
  sliding: boolean;
  /** View yaw (radians): crouching and sliding lean the body ahead of / behind the feet along it. */
  yaw: number;
  /** Horizontal speed (m/s) and whether the player is on the ground: the run bob lowers the upper body. */
  speed: number;
  grounded: boolean;
  /** Legs tucked for the air (0..1): airborne off the walls, or mantling. */
  air: number;
}

/** A displayed player as a bullet target: the stance comes from the snapshot flags. */
export function shotTargetOf(p: InterpolatedPlayer): ShotTarget {
  return {
    id: p.id,
    pos: p.pos,
    crouchFrac: (p.flags & PlayerFlags.CROUCHED) !== 0 ? 1 : 0,
    sliding: (p.flags & PlayerFlags.SLIDING) !== 0,
    yaw: p.yaw,
    speed: Math.hypot(p.vel.x, p.vel.z),
    grounded: (p.flags & PlayerFlags.ON_GROUND) !== 0,
    // The rig tucks its legs in the air (not on a wall) and while mantling.
    air:
      (p.flags & PlayerFlags.MANTLING) !== 0 ||
      (p.flags & (PlayerFlags.ON_GROUND | PlayerFlags.WALLRUN_LEFT | PlayerFlags.WALLRUN_RIGHT)) === 0
        ? 1
        : 0,
  };
}

/** `rayHitboxes`: `[distance, zone]` or empty for a miss; zone 0 head, 1 body, 2 limb. */
export type HitboxRay = (
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  target: ShotTarget,
) => ArrayLike<number>;

export interface PlayerHit {
  id: number;
  dist: number;
  zone: number;
}

/**
 * Nearest player hit along the ray within `maxDist` (the world hit distance, or the shot range),
 * or null when a wall or nothing is closer.
 */
export function nearestPlayerHit(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  targets: readonly ShotTarget[],
  ray: HitboxRay,
): PlayerHit | null {
  let best: PlayerHit | null = null;
  let limit = maxDist;
  for (const t of targets) {
    const r = ray(ox, oy, oz, dx, dy, dz, limit, t);
    if (r.length < 2) continue;
    const dist = r[0] as number;
    if (best === null || dist < best.dist) {
      best = { id: t.id, dist, zone: r[1] as number };
      limit = dist;
    }
  }
  return best;
}

/** Flags that make a displayed player a valid bullet target (alive, not spawn protected). */
export function isShootable(flags: number): boolean {
  return (flags & PlayerFlags.ALIVE) !== 0 && (flags & PlayerFlags.SPAWN_PROTECTED) === 0;
}

/**
 * Whether the server lets `shooterTeam` damage `targetTeam`: free-for-all (`none`) and enemy
 * teams always, teammates only with friendly fire.
 */
export function canDamage(shooterTeam: string, targetTeam: string, friendlyFire: boolean): boolean {
  if (shooterTeam === "none" || targetTeam === "none") return true;
  return shooterTeam !== targetTeam || friendlyFire;
}
